/* --------------------------------------------------------------------------
   Condensing sticky header — v2 (flicker fix)

   Shrinks the sticky header once the visitor scrolls past the top of the page,
   and adds an "All categories" button that re-opens the category bar on
   desktop while condensed.

   Why v2 exists — three things made the header flicker / ghost while scrolling:

   1. LAYOUT PROPERTIES WERE ANIMATED ON A position:sticky ELEMENT.
      v1 transitioned `padding-top/padding-bottom` on .header and `max-height`
      on the logo. For ~180ms the sticky box changed height on every frame, so
      the whole document reflowed on every frame while the compositor was busy
      scrolling. The compositor then presents a stale header texture at the new
      scroll offset — that is the "two headers offset from each other" artefact.
      Fix: the size change is now instantaneous (one frame), transitions off.

   2. WORK WAS DONE ON THE MAIN THREAD DURING SCROLL.
      v1 ran a scroll listener that called offsetHeight (forced synchronous
      layout) and rewrote a custom property used by an `[id] { scroll-margin-top }`
      rule — a selector matching every element on the page, so every write
      invalidated style for the entire document, mid-scroll.
      Fix: no scroll listener at all. Two IntersectionObserver sentinels do the
      threshold detection off the main thread, and the anchor offset is written
      straight to html.style.scrollPaddingTop (one non-inherited property),
      only when it actually changed, and never during a scroll.

   3. THE TOGGLE COULD OSCILLATE.
      Condensing removes ~85px of document height. If the two thresholds sit
      closer together than that, the browser's scroll clamp can push you back
      across the other threshold — condense, expand, condense, forever.
      Fix: the height delta is measured at runtime and the condense threshold
      is pushed out until it clears the delta with room to spare. (This also
      lets us drop v1's height-locking hack, which reserved the full header
      height and left an invisible click-blocking strip over the page.)

   Self-contained: styles are injected below, nothing depends on theme.js
   internals — it only toggles classes on the <html> element.

   Loaded from layout/theme.liquid with:
     <script src="{{ 'condensed-header.js' | asset_url }}" defer></script>
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  var MIN_CONDENSE_AT = 180; // scrolled past this many px -> condense
  var EXPAND_AT       = 40;  // scrolled back above this -> full size
  var SAFETY_GAP      = 60;  // clearance required on top of the height delta

  var condenseAt = MIN_CONDENSE_AT;

  var CSS = [
    ':root{--ip-condensed-logo-height:34px;--ip-header-height:60px}',

    /* (1) Never animate a layout property on a sticky element. */
    '.shopify-section__header .header{transition:none !important}',
    '.shopify-section__header .header__logo-image{transition:none !important}',

    /* Give the sticky header its own compositing layer so its repaints never
       blend with the page content scrolling underneath. will-change:opacity
       promotes WITHOUT creating a containing block, so position:fixed children
       (mini-cart, mobile menu, dropdowns) keep positioning correctly — which is
       why this is not will-change:transform. */
    '.shopify-section__header{will-change:opacity}',

    /* Condensed state — applied in a single frame, no transition. */
    '.ip-header-condensed .shopify-section__header .header{padding-top:8px;padding-bottom:8px}',
    '.ip-header-condensed .shopify-section__header .header__logo-image{max-height:var(--ip-condensed-logo-height);height:auto;width:auto}',

    '@media screen and (min-width:1000px){',
    '.ip-header-condensed .shopify-section__header .nav-bar{display:none}',
    '.ip-header-condensed.ip-nav-open .shopify-section__header .nav-bar{display:block}',
    '.ip-header-condensed .ip-cats-toggle{display:inline-flex}',
    '}',

    /* Scroll sentinels (2) — measured by IntersectionObserver, never painted. */
    '.ip-sentinel{position:absolute;left:0;top:0;width:1px;opacity:0;pointer-events:none;z-index:-1}',

    /* "All categories" toggle */
    '.ip-cats-toggle{display:none;align-items:center;gap:8px;flex:0 0 auto;margin:0 24px 0 8px;',
    'padding:10px 14px;border:1px solid rgba(255,255,255,.3);border-radius:4px;background:transparent;',
    'color:inherit;font:inherit;font-size:14px;line-height:1;cursor:pointer;white-space:nowrap;',
    '-webkit-appearance:none;appearance:none}',
    '.ip-cats-toggle:hover,.ip-cats-toggle[aria-expanded="true"]{border-color:currentColor}',
    '.ip-cats-toggle:focus-visible{outline:2px solid currentColor;outline-offset:2px}',
    '.ip-cats-toggle svg{width:16px;height:12px;fill:currentColor;flex:0 0 auto}',
    '@media screen and (max-width:1200px){',
    '.ip-cats-toggle span{display:none}',
    '.ip-cats-toggle{padding:10px 12px;margin-right:16px}',
    '}'
  ].join('');

  var root      = document.documentElement;
  var section   = null;
  var inner     = null;
  var navBar    = null;
  var toggle    = null;
  var condensed = false;

  var condenseSentinel = null;
  var expandSentinel   = null;
  var observer         = null;
  var lastOffset       = -1;
  var resizeQueued     = false;

  /* ---------------------------------------------------------------- styles */

  function addStyles() {
    if (document.getElementById('ip-condensed-header-styles')) return;
    var style = document.createElement('style');
    style.id = 'ip-condensed-header-styles';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  /* ------------------------------------------------- anchor scroll offset */
  /* Publishes the visible header height so in-page anchors do not land
     underneath it. Writes a single non-inherited property, and only when the
     value actually changed — never from a scroll handler. */

  function measure() {
    if (!section) return;
    var header = section.querySelector('.header');
    var h = header ? header.offsetHeight : section.offsetHeight;
    if (navBar && navBar.offsetParent !== null) h += navBar.offsetHeight;
    if (h === lastOffset) return;
    lastOffset = h;
    root.style.scrollPaddingTop = (h + 12) + 'px';
    root.style.setProperty('--ip-header-height', h + 'px'); // kept for compatibility
  }

  /* --------------------------------------------------------- nav bar toggle */

  function openNav() {
    if (!navBar) return;
    root.classList.add('ip-nav-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    measure();
  }

  function closeNav() {
    root.classList.remove('ip-nav-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    measure();
  }

  /* --------------------------------------------------------- condense state */

  function setCondensed(next) {
    if (next === condensed) return;
    condensed = next;
    root.classList.toggle('ip-header-condensed', next);
    if (!next) closeNav();
    measure();
  }

  /* (3) Measure how much height condensing actually removes, and push the
     condense threshold out until it clears that delta with room to spare, so
     the browser's scroll clamp can never bounce us back over EXPAND_AT. */

  function calibrate() {
    if (!section) return;
    var wasCondensed = root.classList.contains('ip-header-condensed');
    var wasNavOpen   = root.classList.contains('ip-nav-open');

    root.classList.remove('ip-header-condensed', 'ip-nav-open');
    var expandedHeight = section.offsetHeight;

    root.classList.add('ip-header-condensed');
    var condensedHeight = section.offsetHeight;

    root.classList.toggle('ip-header-condensed', wasCondensed);
    if (wasCondensed && wasNavOpen) root.classList.add('ip-nav-open');

    var delta = Math.max(0, expandedHeight - condensedHeight);
    condenseAt = Math.max(MIN_CONDENSE_AT, EXPAND_AT + delta + SAFETY_GAP);
  }

  /* ------------------------------------------------------------- sentinels */

  function makeSentinel(className, height) {
    var el = document.createElement('div');
    el.className = 'ip-sentinel ' + className;
    el.setAttribute('aria-hidden', 'true');
    el.style.height = height + 'px';
    document.body.appendChild(el);
    return el;
  }

  function onIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (entry.target === condenseSentinel) {
        // Sentinel spans 0..condenseAt, so it stops intersecting the viewport
        // exactly when scrollY passes condenseAt.
        if (!entry.isIntersecting) setCondensed(true);
      } else if (entry.target === expandSentinel) {
        if (entry.isIntersecting) setCondensed(false);
      }
    }
  }

  function buildSentinels() {
    if (!('IntersectionObserver' in window)) return false;

    if (observer) observer.disconnect();
    if (condenseSentinel && condenseSentinel.parentNode) condenseSentinel.parentNode.removeChild(condenseSentinel);
    if (expandSentinel && expandSentinel.parentNode) expandSentinel.parentNode.removeChild(expandSentinel);

    condenseSentinel = makeSentinel('ip-sentinel--condense', condenseAt);
    expandSentinel   = makeSentinel('ip-sentinel--expand', EXPAND_AT);

    observer = new IntersectionObserver(onIntersect, { threshold: 0 });
    observer.observe(condenseSentinel);
    observer.observe(expandSentinel);
    return true;
  }

  /* Fallback for browsers without IntersectionObserver: a throttled scroll
     listener that does nothing but compare a number and toggle a class. */

  function installScrollFallback() {
    var ticking = false;
    function check() {
      ticking = false;
      var y = window.pageYOffset || root.scrollTop || 0;
      if (!condensed && y > condenseAt) setCondensed(true);
      else if (condensed && y < EXPAND_AT) setCondensed(false);
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(check);
    }, { passive: true });
    check();
  }

  /* ---------------------------------------------------- categories toggle */

  function buildToggle() {
    toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ip-cats-toggle';
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'All categories');

    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 20 14');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    [0, 6, 12].forEach(function (y) {
      var bar = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      bar.setAttribute('x', '0');
      bar.setAttribute('y', String(y));
      bar.setAttribute('width', '20');
      bar.setAttribute('height', '2');
      svg.appendChild(bar);
    });
    toggle.appendChild(svg);

    var label = document.createElement('span');
    label.textContent = 'All categories';
    toggle.appendChild(label);

    toggle.addEventListener('click', function (event) {
      event.stopPropagation();
      if (root.classList.contains('ip-nav-open')) closeNav();
      else openNav();
    });

    var logo = inner.querySelector('.header__logo');
    if (logo && logo.nextSibling) inner.insertBefore(toggle, logo.nextSibling);
    else inner.appendChild(toggle);
  }

  function onDocumentClick(event) {
    if (!root.classList.contains('ip-nav-open')) return;
    if (toggle && toggle.contains(event.target)) return;
    if (navBar && navBar.contains(event.target)) return;
    closeNav();
  }

  /* ------------------------------------------------------------------ init */

  function init() {
    section = document.querySelector('.shopify-section__header');
    if (!section) return;

    inner  = section.querySelector('.header__inner');
    navBar = section.querySelector('.nav-bar');

    if (inner && navBar && !section.querySelector('.ip-cats-toggle')) {
      buildToggle();
    }

    calibrate();
    measure();

    if (!buildSentinels()) installScrollFallback();
  }

  function onResize() {
    if (resizeQueued) return;
    resizeQueued = true;
    window.requestAnimationFrame(function () {
      resizeQueued = false;
      calibrate();
      lastOffset = -1;
      measure();
      if (condenseSentinel) condenseSentinel.style.height = condenseAt + 'px';
    });
  }

  function boot() {
    addStyles();
    init();

    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('orientationchange', onResize, { passive: true });
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.keyCode === 27) closeNav();
    });

    document.addEventListener('shopify:section:load', function (event) {
      if (event.target && event.target.classList.contains('shopify-section__header')) {
        root.classList.remove('ip-header-condensed', 'ip-nav-open');
        condensed  = false;
        toggle     = null;
        lastOffset = -1;
        init();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();